import { DotsFilledIcon } from '@approvalmax/ui';
import { Button, Dropdown, Menu, useConfirmation } from '@approvalmax/ui/src/components';
import { memo, useCallback, useMemo } from 'react';
import { useFormContext } from 'react-hook-form';
import { GetKyc, useDeletePerson, useKycOptimisticUpdate } from 'shared/data/walletsKyc/v1';
import { useCompanyIdBasedOnUrl } from 'shared/hooks';
import { z } from 'zod';

import type { FormValues } from '../../../../resources/types';
import { personSchema } from '../../schema';
import { getRoles } from '../PersonsTable/PersonsTable.helpers';
import { messages } from './PersonRowControls.messages';

export const PersonRowControls = memo<z.infer<typeof personSchema> & { id: string }>((props) => {
    const { id } = props;

    const { companyId } = useCompanyIdBasedOnUrl();
    const { mutate: deletePerson } = useDeletePerson();
    const form = useFormContext<FormValues>();
    const persons = form.watch('people.persons');
    const person = useMemo(() => persons.find((person) => person.personId === id), [persons, id]);

    const { confirmation } = useConfirmation({
        confirmTitle: messages.removeUser,
        type: 'negative',
        confirmButtonMessage: messages.remove,
        confirmMessage: person?.verifyNeeded ? (
            <div>
                <span>{messages.youAreAboutToRemove({ role: getRoles(person?.roles || []) })}</span>

                <br />

                <span>{messages.afterDeleting}</span>
            </div>
        ) : (
            messages.areYouSure
        ),
    });

    const updateKyc = useKycOptimisticUpdate();

    const handleEdit = useCallback(() => {
        console.info('editing', id);
    }, [id]);

    const handleRemove = useCallback(() => {
        void confirmation()
            .then(() => {
                deletePerson({ params: { path: { organizationId: companyId, personId: id } } });

                updateKyc(
                    (previousKyc: NonNullable<GetKyc['response']>) => ({
                        ...previousKyc,
                        data: {
                            ...previousKyc?.data,
                            people: {
                                ...previousKyc?.data?.people,
                                persons: (previousKyc?.data?.people?.persons || []).filter(
                                    (person) => person.personId !== id
                                ),
                            },
                        },
                    }),
                    { path: { organizationId: companyId } }
                );
            })
            .catch(() => {});
    }, [companyId, confirmation, deletePerson, id, updateKyc]);

    return (
        <Dropdown
            width='max-content'
            activator={
                <Button icon noPadding variant='text' color='blue100'>
                    <DotsFilledIcon />
                </Button>
            }
        >
            <Menu size='small'>
                <Menu.Item name={messages.edit} onClick={handleEdit} />

                <Menu.Item name={messages.remove} onClick={handleRemove} />
            </Menu>
        </Dropdown>
    );
});

PersonRowControls.displayName = 'PersonRowControls';
